import React from "react";
import { Http, HttpOptions } from "../../../utils/Http";
import { GetJobProgressResponse } from "./BackgroundJobs";
import { Modal, ProgressBar, Button } from "@countysuite/ui";
import { RootURL } from "../../../utils/Globals";
import "./TsJobProgressModal.scss";

type Props = {
  show?: boolean;
  jobKey: string;
  onJobError?: (error: string) => void;
  onJobCancel: () => void;
  onClose: (percent: number) => void;
  onJobDone: () => void;
  animation?: boolean;
};

type State = {
  timeout: number;
  progressBarMessage?: string;
  error?: boolean;
  percent: number;
  title: string;

  downloadFileName: string;
  downloadKey: string;
};

function getDownloadFileUrl(jobKey: string) {
  return (
    RootURL + `/BackgroundJobs/DownloadFile?cleanupJob=true&jobKey=${jobKey}`
  );
}

export class TsJobProgressModal extends React.Component<Props, State> {
  state: State = {
    timeout: 0,
    percent: 0,
    title: "",
    downloadFileName: "",
    downloadKey: "",
  };

  onCancelClick = () => {
    if (!this.props.jobKey) return;
    window.clearTimeout(this.state.timeout);
    //not awaiting, we don't care about the response object
    const options: HttpOptions = {
      url: "BackgroundJobs/CancelJob",
      data: { jobKey: this.props.jobKey },
    };
    Http.post(options);
    if (this.props.onJobCancel) {
      this.props.onJobCancel();
    }
  };

  onCloseModal = () => {
    this.props.onClose(this.state.percent);
  };

  pollJobStatus = async () => {
    if (!this.props.jobKey) return;

    const options: HttpOptions = {
      url: `BackgroundJobs/GetJobProgress?jobKey=${this.props.jobKey}`,
    };
    const response = await Http.get<GetJobProgressResponse>(options);
    const info = response.info;

    if (info == null) {
      this.setState({
        percent: 0,
        progressBarMessage: "Unable to find currently running background task.",
        error: true,
        title: "Background Task",
      });
      if (this.props.onJobError)
        this.props.onJobError(
          "Unable to find currently running background task."
        );
      return;
    }

    this.setState({
      percent: info.progressBarPercentage || 0,
      progressBarMessage: info.errorMessage || info.progressBarMessage,
      title: info.jobLabel,
      error: !!info.errorMessage.length,
    });
    if (info.errorMessage.length > 0 && this.props.onJobError) {
      this.props.onJobError(info.errorMessage);
      return;
    } else if (info.isCancelled && this.props.onJobCancel) {
      this.props.onJobCancel();
      return;
    } else if (info.isDone && this.props.onJobDone) {
      if (info.downloadFileName) {
        this.setState({ downloadFileName: info.downloadFileName }, () =>
          Http.post({
            url: "BackgroundJobs/CancelJob",
            data: { jobKey: this.props.jobKey },
          })
        );
      }
      this.props.onJobDone();
      return;
    }

    this.setupPolling();
  };

  setupPolling = (): number => {
    if (this.props.jobKey) {
      const timeout = window.setTimeout(this.pollJobStatus, 1250);
      this.setState({ timeout: timeout });
      return timeout;
    } else return 0;
  };

  setupNewJobState = () => {
    this.setState(function (prevState) {
      let newState: State = {
        ...prevState,
        error: false,
        progressBarMessage: "Running",
        percent: 0,
      };
      return newState;
    });
  };

  componentDidMount = () => {
    if (this.props.jobKey) {
      this.setupPolling();
      this.setupNewJobState();
    }
  };

  componentDidUpdate = (prevProps: Props) => {
    if (!this.props.jobKey) {
      window.clearTimeout(this.state.timeout);
    } else if (prevProps.jobKey !== this.props.jobKey) {
      window.clearTimeout(this.state.timeout);
      this.setupPolling();
      this.setupNewJobState();
    }
  };

  componentWillUnmount = () => {
    if (this.state.timeout) {
      window.clearTimeout(this.state.timeout);
    }
  };

  render() {
    const closeBtnText = this.state.percent >= 100 ? "Finish" : "Close Page";
    const closeBtnColor = this.state.percent >= 100 ? "success" : "default";

    

    let downloadLink: JSX.Element | null = null;
    if (this.state.downloadFileName && this.props.jobKey) {
      downloadLink = (
        <div style={{ textAlign: "center" }}>
          <a href={getDownloadFileUrl(this.props.jobKey)}>
            Download {this.state.downloadFileName}
          </a>
        </div>
      );
    }

    return (
      <Modal
        show={this.props.show}
        onHide={this.onCloseModal}
        title={this.state.title || "Please wait"}
        animation={this.props.animation || false}
        buttons={
          <>
            <Button onClick={this.onCloseModal} color={closeBtnColor}>
              {closeBtnText}
            </Button>
            <Button
              disabled={!this.props.jobKey || this.state.percent >= 100}
              onClick={this.onCancelClick}
              color="danger"
            >
              Pause
            </Button>
          </>
        }
      >
        {this.props.children}
        <ProgressBar
          message={this.state.progressBarMessage || ""}
          percent={this.state.percent}
          error={this.state.error || false}
        />
        {downloadLink}
      </Modal>
    );
  }
}
